import { Component, OnInit, ViewChild, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommunityHeaderComponent } from '../community-header/community-header.component';
import { CreatePostComponent } from '../create-post/create-post.component';
import { FeedComponent } from '../feed/feed.component';
import { CommunityService } from '../../../services/community.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FreelancerDataService } from '../../../services/freelancer-data.service';
import { ToastrService } from 'ngx-toastr';
import { PostStoreService } from '../home/postService/post-store.service';
import { SocketService } from '../../../services/socket.service';
import { AuthService } from '../../guards/service/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    CommunityHeaderComponent,
    CreatePostComponent,
    FeedComponent
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {
  community: any;
  spaceID: any;

  profilePhoto = 'https://kontact-app-public.s3-ap-southeast-1.amazonaws.com/images/Sign-Up-Image.jpg';
  slug: string | null | undefined;
  communityId: string | null | undefined;
  communityID!: number;

  loadingPosts = false;
  hasMore = true;
  cursor: number | null = null;   // <-- drive paging from server nextCursor
  limit = 20;

  @ViewChild(FeedComponent) feedComponent!: FeedComponent;
  communityStats: any;

  userId: number | null | undefined;
  IsRecruiter = false;

  // keep a local snapshot for merging next pages (no need to modify store)
  private postsSnapshot: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private communityService: CommunityService,
    private router: Router,
    private dataService: FreelancerDataService,
    private toastr: ToastrService,
    private postStore: PostStoreService,
    private socketService: SocketService,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    // keep local snapshot in sync with the store
    this.postStore.posts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(arr => this.postsSnapshot = Array.isArray(arr) ? arr : []);

    // capture current user id for socket reactions
    this.userId = this.authService.getUserId();

    const wasReloaded = sessionStorage.getItem('reloaded') === 'true';
    if (wasReloaded) {
      sessionStorage.removeItem('reloaded');
    }

    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const routeSlug = params.get('slug');
        const routeCommunityId = params.get('communityId');

        if (!routeSlug || !routeCommunityId) {
          this.toastr.error('Invalid community URL or missing parameters.');
          return;
        }

        localStorage.setItem('slug', routeSlug);
        localStorage.setItem('communityId', routeCommunityId);

        this.slug = routeSlug;
        this.communityId = routeCommunityId;
        this.communityID = +routeCommunityId;

        // connect socket, join when connected
        this.socketService.connect();
        this.socketService.getConnectionStatus()
          .pipe(takeUntil(this.destroy$))
          .subscribe((connected) => {
            if (connected && this.communityID) {
              this.socketService.joinCommunity(this.communityID);
            }
          });

        // SOCKET: new_post (top-level or reply)
        this.socketService.listenToNewPost()
          .pipe(takeUntil(this.destroy$))
          .subscribe((data: any) => {
            if (!data?.post) return;

            const post = {
              ...data.post,
              replies: [],
              showReplies: false,
              repliesLoaded: false
            };

            // If it's a reply, handle parent update
            if (data.is_reply && post.parent_post_id != null) {
              const parentId = Number(post.parent_post_id);
              let inserted = false;

              // Try to insert into in-memory parent
              inserted = this.postStore.insertReplyDirectly(parentId, post);

              // If this reply is **my** apply to a job, mark applied on the parent card
              if (this.userId && post.user_id === this.userId) {
                this.postStore.markAppliedIfJob(parentId);
              }

              // If parent thread isn't open/loaded, open it (loads replies)
              if (!inserted && this.feedComponent) {
                this.feedComponent.toggleReply({
                  replyId: parentId,
                  replyDepth: (post.reply_depth || 1) - 1,
                  parentPostId: parentId
                });
              }

              // Fallback: if it's *my* reply and we couldn't insert (parent not in store),
              // force a light refresh so the Apply button hides immediately.
              if (!inserted && this.userId && post.user_id === this.userId) {
                this.loadInitialPosts();
              }
            } else {
              // top-level post
              this.postStore.addPost(post);
            }
          });

        // SOCKET: post_updated (edits) -> refresh list if on home
        this.socketService.listenToPostUpdated()
          .pipe(takeUntil(this.destroy$))
          .subscribe((data: any) => {
            if (!data?.post || !data?.id) return;
            const currentUrl = this.router.url;
            const isCommunityHome = /^\/[^\/]+\/\d+\/home$/.test(currentUrl);
            if (isCommunityHome) {
              this.loadInitialPosts();
            }
          });

        // Load community, then avatar/active/posts/recruiter
        this.communityService.getCommunityBySlug(routeSlug)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              this.community = res?.data || null;
              this.spaceID = this.community?.Spaces?.id;

              if (this.spaceID) {
                localStorage.setItem('spaceID', this.spaceID);
                this.getFreelancerAvatar();
                this.updateUserActive();
                this.loadInitialPosts();
                this.getRecruiterStatus();
              }
            },
            error: () => {
              this.toastr.error('Community not found or error occurred.');
            }
          });

        this.getCommunityStats(this.communityID);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateUserActive(){
    const headerValue = this.spaceID;
    this.dataService
      .updateUserActive(headerValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => {}, error: () => {} });
  }

  getFreelancerAvatar(): void {
    this.dataService.getFreelancerAvatar({}, this.spaceID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.profilePhoto = res.data,
        error: (err) => { console.error('Error fetching consultant avatar:', err); }
      });
  }

  loadInitialPosts(): void {
    this.cursor = null;
    this.hasMore = true;
    this.loadingPosts = false; // reset guard
    this.postStore.setPosts([]);
    this.getCommunityPosts();
  }

  private appendUnique(existing: any[], incoming: any[]): any[] {
    const seen = new Set<number>(existing.map(p => p.id));
    const fresh: any[] = [];
    for (const p of incoming) {
      if (p && typeof p.id === 'number' && !seen.has(p.id)) {
        seen.add(p.id);
        fresh.push(p);
      }
    }
    return existing.concat(fresh);
  }

  getCommunityPosts(): void {
    if (this.loadingPosts || !this.hasMore) return;
    this.loadingPosts = true;

    const params: any = { limit: this.limit };
    if (this.cursor != null) params.cursor = this.cursor;

    this.communityService.getCommunityPosts(this.communityID, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // Accept both common shapes:
          // A) { success, data: Post[], nextCursor }
          // B) { success, data: { data: Post[], nextCursor } }
          const payload = (res?.data && (Array.isArray(res.data) || res.data?.data))
            ? res.data
            : res;

          const posts = Array.isArray(payload?.data)
            ? payload.data
            : (Array.isArray(payload) ? payload : []);

          const nextCursor = (payload?.nextCursor !== undefined)
            ? payload.nextCursor
            : (res?.nextCursor !== undefined ? res.nextCursor : null);

          // normalize UI flags
          const cleaned = posts.map((post: any) => ({
            ...post,
            replies: [],
            showReplies: false,
            repliesLoaded: false,
          }));

          if (this.cursor == null) {
            this.postStore.setPosts(cleaned);
          } else {
            const merged = this.appendUnique(this.postsSnapshot, cleaned);
            this.postStore.setPosts(merged);
          }

          // **Drive paging from server nextCursor**
          this.cursor = (nextCursor === undefined ? null : nextCursor);
          this.hasMore = this.cursor !== null;
        },
        error: () => {
          this.toastr.error('Error loading community posts.');
          this.hasMore = false;
        },
        complete: () => {
          this.loadingPosts = false;
        }
      });
  }

  getCommunityStats(communityID: number): void {
    this.communityService.getCommunityStats(communityID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => { this.communityStats = res?.data; },
        error: () => { this.toastr.error('Error Getting Community Stats'); }
      });
  }

  getRecruiterStatus() {
    this.userId = this.authService.getUserId();

    const data = {
      user_id: this.userId,
      space_id: this.spaceID,
      community_id: this.communityID
    };
    this.communityService.getRecruiterStatus(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => { this.IsRecruiter = res.data.is_recruiter; },
        error: () => {}
      });
  }

  // More robust scroll calc (different browsers)
  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (this.loadingPosts || !this.hasMore) return;

    const el = document.scrollingElement || document.documentElement;
    const scrollPosition = el.scrollTop + window.innerHeight;
    const documentHeight = el.scrollHeight;
    const buffer = 200;

    if (scrollPosition + buffer >= documentHeight) {
      this.getCommunityPosts();
    }
  }
}
